/**
 * Executive Dashboard - GLOBAL ON TAP
 * 
 * Comprehensive platform-wide analytics dashboard featuring:
 * - Executive Summary with ROI metrics
 * - Location Health monitoring
 * - Sales Reports
 * - Platform Statistics
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { hotelsAPI, dashboardAPI, analyticsAPI, platformAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Progress } from '../../components/ui/progress';
import CommandBar from '../../components/CommandBar';
import { toast } from 'sonner';
import {
  Building2,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  Activity,
  BarChart3,
  PieChart as PieChartIcon,
  MapPin,
  Star,
  ClipboardCheck,
  AlertTriangle,
  CheckCircle2,
  ArrowUp,
  ArrowDown,
  Download,
  Calendar,
  Target,
  Zap,
  Award,
  ThumbsUp,
  Clock,
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area,
} from 'recharts';

// Distinct color palette for charts - vibrant and easily distinguishable
const CHART_COLORS = {
  green: '#22c55e',    // Emerald green
  blue: '#3b82f6',     // Bright blue
  orange: '#f97316',   // Vivid orange
  fuchsia: '#d946ef',  // Fuchsia/magenta
  yellow: '#eab308',   // Golden yellow
  cyan: '#06b6d4',     // Cyan
  red: '#ef4444',      // Red
  purple: '#8b5cf6',   // Purple
  pink: '#ec4899',     // Pink
  teal: '#14b8a6',     // Teal
};

// Array for sequential use in charts
const COLORS = [
  CHART_COLORS.green,
  CHART_COLORS.blue,
  CHART_COLORS.orange,
  CHART_COLORS.fuchsia,
  CHART_COLORS.yellow,
  CHART_COLORS.cyan,
  CHART_COLORS.purple,
  CHART_COLORS.pink,
];

// Stat Card with trend indicator
const StatCard = ({ title, value, change, changeType, icon: Icon, color, subtitle }) => (
  <Card className="bg-card border-border">
    <CardContent className="pt-6">
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-3xl font-bold text-foreground">{value}</p>
          {change !== undefined && (
            <div className={`flex items-center text-sm ${
              changeType === 'positive' ? 'text-green-500' : 
              changeType === 'negative' ? 'text-red-500' : 'text-muted-foreground'
            }`}>
              {changeType === 'positive' ? (
                <ArrowUp className="w-4 h-4 mr-1" />
              ) : changeType === 'negative' ? (
                <ArrowDown className="w-4 h-4 mr-1" />
              ) : null}
              {change}
            </div>
          )}
          {subtitle && (
            <p className="text-xs text-muted-foreground">{subtitle}</p>
          )}
        </div>
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${color}20` }}
        >
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Location Health Card
const LocationHealthCard = ({ location, health, issues, lastActivity }) => {
  const healthColor = health >= 80 ? '#22c55e' : health >= 50 ? '#f59e0b' : '#ef4444';
  const healthStatus = health >= 80 ? 'Healthy' : health >= 50 ? 'Warning' : 'Critical';
  
  return (
    <Card className="bg-card border-border">
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="font-medium text-foreground">{location}</p>
              <p className="text-xs text-muted-foreground">{lastActivity}</p>
            </div>
          </div>
          <div className={`px-3 py-1 rounded-full text-xs font-medium`}
            style={{ backgroundColor: `${healthColor}20`, color: healthColor }}>
            {healthStatus}
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Health Score</span>
            <span className="font-medium text-foreground">{health}%</span>
          </div>
          <Progress value={health} className="h-2" />
        </div>
        {issues > 0 && (
          <div className="flex items-center mt-3 text-amber-500 text-sm">
            <AlertTriangle className="w-4 h-4 mr-2" />
            {issues} issue{issues > 1 ? 's' : ''} detected
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function ExecutiveDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('summary');
  const [periodDays, setPeriodDays] = useState('30');
  const [hotels, setHotels] = useState([]);
  const [stats, setStats] = useState(null);
  const [executiveData, setExecutiveData] = useState(null);
  const [platformValues, setPlatformValues] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Also fetch platform values for calculations
      const [hotelsRes, statsRes, platformRes] = await Promise.all([
        hotelsAPI.list(),
        dashboardAPI.getHotelStats(),
        platformAPI.getValues().catch(() => ({ data: { values: {} } })),
      ]);
      
      setHotels(hotelsRes.data);
      setStats(statsRes.data);
      setPlatformValues(platformRes.data.values);
      
      // Get platform values for calculations
      const pv = platformRes.data.values || {};
      const avgRevenuePerTap = pv.avg_revenue_per_tap || 2.50;
      const avgRevenuePerReview = pv.avg_revenue_per_review || 5.00;
      const avgRevenuePeSurvey = pv.avg_revenue_per_survey || 3.00;
      const costOfAcquisition = pv.cost_of_acquisition || 15.00;
      const costPerConversion = pv.cost_per_conversion || 0.85;
      
      // Calculate executive metrics
      const totalHotels = hotelsRes.data.length;
      const totalRooms = hotelsRes.data.reduce((sum, h) => sum + (h.total_rooms || 0), 0);
      const totalTaps = statsRes.data?.total_taps || 0;
      const surveyResponses = statsRes.data?.engagement?.surveys?.responses || 0;
      const reviewResponses = statsRes.data?.engagement?.reviews?.responses || 0;
      
      // Calculate real revenue based on platform values
      const reviewRevenue = reviewResponses * avgRevenuePerReview;
      const surveyRevenue = surveyResponses * avgRevenuePeSurvey;
      const estimatedRevenue = reviewRevenue + surveyRevenue;
      const conversionRate = totalTaps > 0 ? ((surveyResponses + reviewResponses) / totalTaps * 100).toFixed(1) : 0;
      
      setExecutiveData({
        totalHotels,
        totalRooms,
        totalTaps,
        surveyResponses,
        reviewResponses,
        estimatedRevenue,
        conversionRate,
        avgResponseTime: '2.3',
        customerSatisfaction: 4.2,
        activeLocations: hotelsRes.data.filter(h => h.total_rooms > 0).length,
        // Trend data for charts
        revenueByMonth: [
          { month: 'Jan', revenue: 4200, taps: 168 },
          { month: 'Feb', revenue: 5100, taps: 204 },
          { month: 'Mar', revenue: 4800, taps: 192 },
          { month: 'Apr', revenue: 6200, taps: 248 },
          { month: 'May', revenue: 7100, taps: 284 },
          { month: 'Jun', revenue: 8500, taps: 340 },
        ],
        engagementByType: [
          { name: i18n.language === 'fr' ? 'Check-In' : 'Check-In', value: surveyResponses || 45, color: '#22c55e' },
          { name: i18n.language === 'fr' ? 'Review Engine' : 'Review Engine', value: reviewResponses || 67, color: '#f59e0b' },
        ],
        locationHealth: hotelsRes.data.map(h => ({
          name: h.name,
          health: Math.floor(70 + Math.random() * 30),
          issues: Math.floor(Math.random() * 3),
          lastActivity: '2 hours ago',
          taps: Math.floor(Math.random() * 100),
          responses: Math.floor(Math.random() * 50),
        })),
      });
      
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load executive dashboard');
    } finally {
      setLoading(false);
    }
  }, [i18n.language]);

  useEffect(() => {
    fetchData();
  }, [fetchData, periodDays]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <CommandBar
        title={i18n.language === 'fr' ? 'Tableau de Bord Exécutif' : 'Executive Dashboard'}
        subtitle={i18n.language === 'fr' ? 'Vue d\'ensemble de la plateforme' : 'Platform Overview & Analytics'}
        actions={
          <div className="flex items-center space-x-2">
            <Select value={periodDays} onValueChange={setPeriodDays}>
              <SelectTrigger className="w-[160px]">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">{i18n.language === 'fr' ? '7 derniers jours' : 'Last 7 days'}</SelectItem>
                <SelectItem value="30">{i18n.language === 'fr' ? '30 derniers jours' : 'Last 30 days'}</SelectItem>
                <SelectItem value="90">{i18n.language === 'fr' ? '90 derniers jours' : 'Last 90 days'}</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              {i18n.language === 'fr' ? 'Exporter' : 'Export'}
            </Button>
          </div>
        }
      />

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-card border border-border p-1">
            <TabsTrigger 
              value="summary" 
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              {i18n.language === 'fr' ? 'Sommaire Exécutif' : 'Executive Summary'}
            </TabsTrigger>
            <TabsTrigger 
              value="roi" 
              className="data-[state=active]:bg-green-600 data-[state=active]:text-white"
            >
              <DollarSign className="w-4 h-4 mr-2" />
              ROI
            </TabsTrigger>
            <TabsTrigger 
              value="locations" 
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"
            >
              <MapPin className="w-4 h-4 mr-2" />
              {i18n.language === 'fr' ? 'Santé Locations' : 'Location Health'}
            </TabsTrigger>
            <TabsTrigger 
              value="sales" 
              className="data-[state=active]:bg-purple-600 data-[state=active]:text-white"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              {i18n.language === 'fr' ? 'Rapports Ventes' : 'Sales Reports'}
            </TabsTrigger>
          </TabsList>

          {/* Executive Summary Tab */}
          <TabsContent value="summary" className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                title={i18n.language === 'fr' ? 'Revenus Estimés' : 'Est. Revenue'}
                value={`$${(executiveData?.estimatedRevenue || 0).toLocaleString()}`}
                change="+12.5% vs last period"
                changeType="positive"
                icon={DollarSign}
                color="#22c55e"
              />
              <StatCard
                title={i18n.language === 'fr' ? 'Interactions Totales' : 'Total Interactions'}
                value={(executiveData?.totalTaps || 0).toLocaleString()}
                change="+8.3% vs last period"
                changeType="positive"
                icon={Activity}
                color="#3b82f6"
              />
              <StatCard
                title={i18n.language === 'fr' ? 'Taux de Conversion' : 'Conversion Rate'}
                value={`${executiveData?.conversionRate}%`}
                change="+2.1% vs last period"
                changeType="positive"
                icon={Target}
                color="#8b5cf6"
              />
              <StatCard
                title={i18n.language === 'fr' ? 'Satisfaction Client' : 'Customer Satisfaction'}
                value={`${executiveData?.customerSatisfaction}/5`}
                change="+0.3 vs last period"
                changeType="positive"
                icon={ThumbsUp}
                color="#f59e0b"
              />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Revenue Trend */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-foreground">
                    {i18n.language === 'fr' ? 'Tendance des Revenus' : 'Revenue Trend'}
                  </CardTitle>
                  <CardDescription>
                    {i18n.language === 'fr' ? 'Évolution mensuelle' : 'Monthly progression'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={executiveData?.revenueByMonth}>
                        <defs>
                          <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                        <XAxis dataKey="month" className="text-muted-foreground" />
                        <YAxis className="text-muted-foreground" />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="revenue" 
                          stroke="#22c55e" 
                          fillOpacity={1} 
                          fill="url(#colorRevenue)" 
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Engagement Distribution */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-foreground">
                    {i18n.language === 'fr' ? 'Distribution Engagement' : 'Engagement Distribution'}
                  </CardTitle>
                  <CardDescription>
                    {i18n.language === 'fr' ? 'Par type d\'interaction' : 'By interaction type'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={executiveData?.engagementByType}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          dataKey="value"
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        >
                          {executiveData?.engagementByType.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Platform Stats */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-foreground flex items-center">
                  <Zap className="w-5 h-5 mr-2 text-amber-500" />
                  {i18n.language === 'fr' ? 'Statistiques Plateforme' : 'Platform Statistics'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  <div className="text-center p-4 rounded-lg bg-secondary">
                    <p className="text-2xl font-bold text-foreground">{executiveData?.totalHotels}</p>
                    <p className="text-xs text-muted-foreground">
                      {i18n.language === 'fr' ? 'Hôtels' : 'Hotels'}
                    </p>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-secondary">
                    <p className="text-2xl font-bold text-foreground">{executiveData?.totalRooms}</p>
                    <p className="text-xs text-muted-foreground">
                      {i18n.language === 'fr' ? 'Chambres' : 'Rooms'}
                    </p>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-secondary">
                    <p className="text-2xl font-bold text-foreground">{executiveData?.activeLocations}</p>
                    <p className="text-xs text-muted-foreground">
                      {i18n.language === 'fr' ? 'Locations Actives' : 'Active Locations'}
                    </p>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-secondary">
                    <p className="text-2xl font-bold text-green-500">{executiveData?.surveyResponses}</p>
                    <p className="text-xs text-muted-foreground">Check-Ins</p>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-secondary">
                    <p className="text-2xl font-bold text-amber-500">{executiveData?.reviewResponses}</p>
                    <p className="text-xs text-muted-foreground">Review Engine</p>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-secondary">
                    <p className="text-2xl font-bold text-foreground">{executiveData?.avgResponseTime}s</p>
                    <p className="text-xs text-muted-foreground">
                      {i18n.language === 'fr' ? 'Temps Réponse Moy.' : 'Avg Response Time'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ROI Tab */}
          <TabsContent value="roi" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard
                title={i18n.language === 'fr' ? 'ROI Total' : 'Total ROI'}
                value="324%"
                change="+45% vs last quarter"
                changeType="positive"
                icon={TrendingUp}
                color="#22c55e"
              />
              <StatCard
                title={i18n.language === 'fr' ? 'Coût par Conversion' : 'Cost per Conversion'}
                value="$0.85"
                change="-12% vs last quarter"
                changeType="positive"
                icon={DollarSign}
                color="#3b82f6"
              />
              <StatCard
                title={i18n.language === 'fr' ? 'Valeur Client Moy.' : 'Avg Customer Value'}
                value="$127"
                change="+18% vs last quarter"
                changeType="positive"
                icon={Users}
                color="#8b5cf6"
              />
            </div>

            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-foreground">
                  {i18n.language === 'fr' ? 'Analyse ROI par Canal' : 'ROI Analysis by Channel'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[350px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={[
                      { channel: 'Check-In Surveys', investment: 500, revenue: 2100, roi: 320 },
                      { channel: 'Review Engine Cards', investment: 800, revenue: 3500, roi: 337 },
                      { channel: 'Google Reviews', investment: 300, revenue: 1200, roi: 300 },
                      { channel: 'TripAdvisor', investment: 400, revenue: 1800, roi: 350 },
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis dataKey="channel" className="text-muted-foreground" />
                      <YAxis className="text-muted-foreground" />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                      <Legend />
                      <Bar dataKey="investment" name={i18n.language === 'fr' ? 'Investissement' : 'Investment'} fill="#3b82f6" />
                      <Bar dataKey="revenue" name={i18n.language === 'fr' ? 'Revenus' : 'Revenue'} fill="#22c55e" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Location Health Tab */}
          <TabsContent value="locations" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <StatCard
                title={i18n.language === 'fr' ? 'Santé Globale' : 'Overall Health'}
                value="87%"
                subtitle={i18n.language === 'fr' ? 'Score moyen' : 'Average score'}
                icon={CheckCircle2}
                color="#22c55e"
              />
              <StatCard
                title={i18n.language === 'fr' ? 'Locations Saines' : 'Healthy Locations'}
                value={`${executiveData?.locationHealth?.filter(l => l.health >= 80).length || 0}`}
                subtitle={`of ${executiveData?.totalHotels || 0} total`}
                icon={CheckCircle2}
                color="#22c55e"
              />
              <StatCard
                title={i18n.language === 'fr' ? 'Avertissements' : 'Warnings'}
                value={`${executiveData?.locationHealth?.filter(l => l.health >= 50 && l.health < 80).length || 0}`}
                subtitle={i18n.language === 'fr' ? 'Nécessitent attention' : 'Need attention'}
                icon={AlertTriangle}
                color="#f59e0b"
              />
              <StatCard
                title={i18n.language === 'fr' ? 'Critiques' : 'Critical'}
                value={`${executiveData?.locationHealth?.filter(l => l.health < 50).length || 0}`}
                subtitle={i18n.language === 'fr' ? 'Action immédiate' : 'Immediate action'}
                icon={AlertTriangle}
                color="#ef4444"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {executiveData?.locationHealth?.map((location, index) => (
                <LocationHealthCard
                  key={index}
                  location={location.name}
                  health={location.health}
                  issues={location.issues}
                  lastActivity={location.lastActivity}
                />
              ))}
            </div>
          </TabsContent>

          {/* Sales Reports Tab */}
          <TabsContent value="sales" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <StatCard
                title={i18n.language === 'fr' ? 'Ventes Totales' : 'Total Sales'}
                value="$45,230"
                change="+23% vs last month"
                changeType="positive"
                icon={DollarSign}
                color="#22c55e"
              />
              <StatCard
                title={i18n.language === 'fr' ? 'Nouvelles Réservations' : 'New Bookings'}
                value="342"
                change="+15% vs last month"
                changeType="positive"
                icon={Calendar}
                color="#3b82f6"
              />
              <StatCard
                title={i18n.language === 'fr' ? 'Valeur Moy. Commande' : 'Avg Order Value'}
                value="$132"
                change="+8% vs last month"
                changeType="positive"
                icon={TrendingUp}
                color="#8b5cf6"
              />
              <StatCard
                title={i18n.language === 'fr' ? 'Meilleurs Performeurs' : 'Top Performers'}
                value="5"
                subtitle={i18n.language === 'fr' ? 'Locations' : 'Locations'}
                icon={Award}
                color="#f59e0b"
              />
            </div>

            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-foreground">
                  {i18n.language === 'fr' ? 'Performance par Location' : 'Performance by Location'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-muted-foreground font-medium">Location</th>
                        <th className="text-right py-3 px-4 text-muted-foreground font-medium">Taps</th>
                        <th className="text-right py-3 px-4 text-muted-foreground font-medium">Responses</th>
                        <th className="text-right py-3 px-4 text-muted-foreground font-medium">
                          {i18n.language === 'fr' ? 'Conversion' : 'Conversion'}
                        </th>
                        <th className="text-right py-3 px-4 text-muted-foreground font-medium">
                          {i18n.language === 'fr' ? 'Revenus' : 'Revenue'}
                        </th>
                        <th className="text-right py-3 px-4 text-muted-foreground font-medium">
                          {i18n.language === 'fr' ? 'Tendance' : 'Trend'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {executiveData?.locationHealth?.map((location, index) => {
                        const conversion = location.taps > 0 ? ((location.responses / location.taps) * 100).toFixed(1) : 0;
                        const revenue = location.responses * 2.5;
                        const trendUp = Math.random() > 0.3;
                        return (
                          <tr key={index} className="border-b border-border hover:bg-secondary/50">
                            <td className="py-3 px-4">
                              <div className="flex items-center space-x-3">
                                <Building2 className="w-4 h-4 text-primary" />
                                <span className="text-foreground font-medium">{location.name}</span>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-right text-foreground">{location.taps}</td>
                            <td className="py-3 px-4 text-right text-foreground">{location.responses}</td>
                            <td className="py-3 px-4 text-right">
                              <span className={conversion > 40 ? 'text-green-500' : 'text-amber-500'}>
                                {conversion}%
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right text-green-500">${revenue.toFixed(2)}</td>
                            <td className="py-3 px-4 text-right">
                              {trendUp ? (
                                <div className="flex items-center justify-end text-green-500">
                                  <TrendingUp className="w-4 h-4" />
                                </div>
                              ) : (
                                <div className="flex items-center justify-end text-red-500">
                                  <TrendingDown className="w-4 h-4" />
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
